import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FlujoFondosPeriodo } from '../../../services/tesoreriaFlujoFondosService';
import { formatCurrency } from '../../../utils/formatters';

interface Props {
  periodos: FlujoFondosPeriodo[];
  onPeriodoClick: (periodo: FlujoFondosPeriodo) => void;
}

const FlujoFondosPeriodosTable: React.FC<Props> = ({ periodos, onPeriodoClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Períodos
        </Typography>
        {periodos.map((p) => (
          <Paper
            key={p.indice}
            elevation={0}
            onClick={() => onPeriodoClick(p)}
            sx={{
              p: 1.75,
              borderRadius: 2,
              border: '1px solid',
              borderColor: p.alertaNegativo ? '#ef5350' : p.alertaExcedente ? '#ff9800' : '#e0e0e0',
              cursor: 'pointer',
              '&:active': { bgcolor: '#f5f5f5' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="subtitle2" fontWeight={600}>
                {p.etiqueta}
              </Typography>
              {p.alertaNegativo ? (
                <Chip size="small" label="Negativo" color="error" />
              ) : p.alertaExcedente ? (
                <Chip size="small" label="Excedente" color="warning" />
              ) : null}
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="success.main">
                +{formatCurrency(p.ingresos)}
              </Typography>
              <Typography variant="caption" color="error.main">
                -{formatCurrency(p.egresos)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" mt={0.5}>
              <Typography variant="body2" color="text.secondary">
                Neto {formatCurrency(p.neto)}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color={p.saldoAcumulado < 0 ? 'error.main' : 'text.primary'}
              >
                Saldo {formatCurrency(p.saldoAcumulado)}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Períodos
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell>Período</TableCell>
              <TableCell align="right">Ingresos</TableCell>
              <TableCell align="right">Egresos</TableCell>
              <TableCell align="right">Neto</TableCell>
              <TableCell align="right">Saldo acum.</TableCell>
              <TableCell align="center">Alerta</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periodos.map((p) => (
              <TableRow
                key={p.indice}
                hover
                onClick={() => onPeriodoClick(p)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: p.alertaNegativo
                    ? 'rgba(239,83,80,0.06)'
                    : p.alertaExcedente
                      ? 'rgba(255,152,0,0.06)'
                      : undefined,
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {p.etiqueta}
                  </Typography>
                  {p.esVencido ? (
                    <Typography variant="caption" color="text.secondary">
                      Incluye vencidos
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right" sx={{ color: '#2e7d32' }}>
                  {formatCurrency(p.ingresos)}
                </TableCell>
                <TableCell align="right" sx={{ color: '#c62828' }}>
                  {formatCurrency(p.egresos)}
                </TableCell>
                <TableCell align="right">{formatCurrency(p.neto)}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 700, color: p.saldoAcumulado < 0 ? '#c62828' : undefined }}
                >
                  {formatCurrency(p.saldoAcumulado)}
                </TableCell>
                <TableCell align="center">
                  {p.alertaNegativo ? (
                    <Chip size="small" label="Cubrir" color="error" />
                  ) : p.alertaExcedente ? (
                    <Chip size="small" label="Invertir" color="warning" />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FlujoFondosPeriodosTable;
